import {
  memo, useCallback, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import Text from '@/components/component-library/Text';
import useIntersectionObserver from '@/hooks/browser/useIntersectionObserver';
import Link from '@/components/component-library/Link';
import { classes } from '@/helpers/styling';

const Option = ({
  value,
  selected,
  onClick,
  href,
  intersectionRoot,
  onEnterView,
  onLeaveView,
  onRenderOption,
  id,
  hidden,
  className,
  children,
  rootMarginAdjustment,
  OptionComponent
}) => {
  const optionRef = useRef();

  useEffect(() => {
    if (onRenderOption) {
      onRenderOption({
        id,
        ref: optionRef
      });
    }
  }, [onRenderOption, id]);

  const handleEnterView = useCallback(() => {
    if (onEnterView) {
      onEnterView({
        id,
        ref: optionRef
      });
    }
  }, [id, onEnterView, optionRef]);

  const handleLeaveView = useCallback(() => {
    if (onLeaveView) {
      onLeaveView({
        id,
        ref: optionRef
      });
    }
  }, [id, onLeaveView, optionRef]);

  const { ref: optionIntersectionRef } = useIntersectionObserver({
    triggerInViewOnceOnly: false,
    onEnterView: handleEnterView,
    onLeaveView: handleLeaveView,
    disable: !(intersectionRoot && (onEnterView || onLeaveView)),
    rootMargin: `0% -${ rootMarginAdjustment || 0 }px 0% 0%`,
    root: intersectionRoot,
    threshold: 1.0,
    delay: 0
  });

  return (
    <Link
      className={ classes('selector-h__option', className, {
        '--is-selected': selected,
        '--is-hidden': hidden
      }) }
      href={ href }
      onClick={ onClick }
      ref={ optionIntersectionRef }
    >
      { OptionComponent
        ? (
          <OptionComponent
            id={ id }
            ref={ optionRef }
            selected={ selected }
            value={ value }
          />
        )
        : (
          <Text
            bold
            className='selector-h__option-text'
            ref={ optionRef }
            useHeaderFont
          >
            { children || value }
          </Text>
        ) }
    </Link>
  );
};

Option.propTypes = {
  value: PropTypes.string,
  selected: PropTypes.bool,
  intersectionRoot: PropTypes.oneOfType([PropTypes.elementType, PropTypes.object]),
  onClick: PropTypes.func,
  onRenderOption: PropTypes.func,
  onEnterView: PropTypes.func,
  onLeaveView: PropTypes.func,
  id: PropTypes.string,
  hidden: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  href: PropTypes.string,
  rootMarginAdjustment: PropTypes.number,
  OptionComponent: PropTypes.element
};

Option.defaultProps = {
  value: '',
  selected: false,
  intersectionRoot: null,
  onClick: undefined,
  onEnterView: undefined,
  onLeaveView: undefined,
  onRenderOption: undefined,
  id: '',
  hidden: false,
  className: '',
  children: null,
  href: '',
  rootMarginAdjustment: 0,
  OptionComponent: undefined
};

export default memo(Option);